import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";

import AuthSplitLayout from "../../Components/Auth/AuthSplitLayout";
import { forgotPassword } from "../../services/authService";

const ForgetPasswordPage = () => {
  const { t } = useTranslation();
  const [loader, setLoader] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  const validationSchema = useMemo(
    () =>
      Yup.object({
        email: Yup.string()
          .email(t("auth.forgot.validation.emailInvalid"))
          .required(t("auth.forgot.validation.emailRequired")),
      }),
    [t]
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email:
        new URLSearchParams(window.location.search).get("email")?.trim() || "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoader(true);
      setErrorMessage("");
      setSuccessMessage("");

      try {
        const response = await forgotPassword(values.email);

        if (response.success) {
          const message = response.message || t("auth.forgot.toast.success");
          setSuccessMessage(message);
          toast.success(message);
          return;
        }

        const fallbackMessage =
          response.message || t("auth.forgot.toast.genericError");
        setErrorMessage(fallbackMessage);
        toast.error(fallbackMessage);
      } catch (error: unknown) {
        const message =
          (error as { data?: string })?.data ||
          t("auth.forgot.toast.genericError");
        setErrorMessage(message);
        toast.error(message);
      } finally {
        setLoader(false);
      }
    },
  });

  useEffect(() => {
    document.title = t("auth.forgot.pageTitle");
  }, [t]);

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} />
      <AuthSplitLayout
        title={t("auth.forgot.title")}
        subtitle={t("auth.forgot.subtitle")}
        headerAction={
          <Link to="/login" className="adatis-auth-split__header-btn">
            <i className="ri-login-box-line" aria-hidden="true" />
            {t("auth.forgot.links.backToLogin")}
          </Link>
        }
        lead={
          <>
            <Alert
              className="border-0 alert-warning mb-0"
              role="status"
              aria-live="polite"
            >
              {t("auth.forgot.hint")}
            </Alert>
            {errorMessage ? (
              <Alert color="danger" className="mt-2 mb-0">
                {errorMessage}
              </Alert>
            ) : null}
            {successMessage ? (
              <Alert color="success" className="mt-2 mb-0">
                {successMessage}
              </Alert>
            ) : null}
          </>
        }
      >
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            validation.handleSubmit();
          }}
        >
          <div className="mb-4">
            <Label className="form-label" htmlFor="email">
              {t("auth.forgot.fields.email.label")}
            </Label>
            <Input
              id="email"
              name="email"
              className="form-control"
              placeholder={t("auth.forgot.fields.email.placeholder")}
              type="email"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.email || ""}
              invalid={
                validation.touched.email && validation.errors.email
                  ? true
                  : false
              }
            />
            {validation.touched.email && validation.errors.email ? (
              <FormFeedback type="invalid">
                {validation.errors.email}
              </FormFeedback>
            ) : null}
          </div>

          <div className="mt-4">
            <Button
              color="primary"
              className="btn btn-primary w-100"
              type="submit"
              disabled={loader}
            >
              {loader ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t("auth.forgot.actions.loading")}
                </>
              ) : (
                t("auth.forgot.actions.submit")
              )}
            </Button>
          </div>
        </Form>
      </AuthSplitLayout>
    </React.Fragment>
  );
};

export default ForgetPasswordPage;
